"use client";

import { Modal } from "@/components/ui/modal";
import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import BackToTopButton from "@/components/ui/back-to-top-button";
import { Highlight } from "@/components/ui/hero-highlight";
import { Link } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";
import { IconBrandAppstore, IconBrandGooglePlay, IconArrowRight } from "@tabler/icons-react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import HeroCarousel from "@/components/ui/hero-carousel";
import { Copy, Check, Share2, Loader2, QrCode } from "lucide-react";
import QRCode from "react-qr-code";
import { useAuth } from "@/contexts/auth-context";
import {
  getCampaignsApi,
  getCampaignDashboardApi,
  type UserDashboard,
  type ReferralSummary,
} from "@/lib/api-user";

function usePlatform() {
  const [platform] = useState<"ios" | "android" | null>(() => {
    if (typeof navigator === "undefined") return null;
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return "ios";
    if (/Android/.test(ua)) return "android";
    return null;
  });
  return platform;
}

// Carousel functionality moved to components/ui/hero-carousel.tsx

const IOS_APP_URL = "https://apps.apple.com/es/app/geesol/id6758011150";
const ANDROID_APP_URL = "https://play.google.com/store/apps/details?id=com.geesol.app&pcampaignid=web_share";

// ─── Referral Dashboard ───────────────────────────────────────────────────────

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col items-start gap-1">
      <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 leading-snug">{label}</span>
      <span className="text-3xl sm:text-4xl font-black text-brand-navy dark:text-white">{value}</span>
    </div>
  );
}

function ReferralRow({ referral, t }: { referral: ReferralSummary; t: ReturnType<typeof useTranslations> }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-gray-100 dark:border-white/10 bg-white dark:bg-surface-dark-raised px-4 py-3 shadow-sm">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-brand-navy dark:text-white">{referral.name || "—"}</p>
        <p className="truncate text-xs text-gray-500 dark:text-gray-400">{referral.email}</p>
      </div>
      <span
        className={`ml-3 shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
          referral.contract
            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
            : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
        }`}
      >
        {referral.contract ? t("dashboard.history.installed") : t("dashboard.history.pending")}
      </span>
    </div>
  );
}

function QRModal({ link, onClose, t }: { link: string; onClose: () => void; t: ReturnType<typeof useTranslations> }) {
  return (
    <Modal isOpen onClose={onClose} className="max-w-xs" closeLabel={t("dashboard.closeQR")}>
      <div className="flex flex-col items-center gap-4 p-6">
        <p className="text-sm font-semibold text-brand-navy dark:text-white pr-6">{t("dashboard.myCode")}</p>
        <div className="rounded-xl bg-white p-3 shadow-inner">
          <QRCode value={link} size={200} />
        </div>
        <p className="max-w-[220px] break-all text-center text-xs text-gray-400">{link}</p>
      </div>
    </Modal>
  );
}

function ReferralDashboard() {
  const t = useTranslations("referralPage");
  const { user, isAuthenticated, isLoading: authLoading, getValidToken } = useAuth();

  const [dashboard, setDashboard] = useState<UserDashboard | null>(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<"noCampaign" | "loadError" | null>(null);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !user) return;

    let cancelled = false;

    async function load() {
      setFetchLoading(true);
      setFetchError(null);
      try {
        const token = await getValidToken();
        if (!token || cancelled) return;

        const campaigns = await getCampaignsApi(user!.id, token);
        if (cancelled) return;

        if (!campaigns || campaigns.length === 0) {
          setFetchError("noCampaign");
          return;
        }

        const camp = campaigns[0];

        const dash = await getCampaignDashboardApi(camp.campaign_url, camp.user_url, token);
        if (!cancelled) setDashboard(dash);
      } catch {
        if (!cancelled) setFetchError("loadError");
      } finally {
        if (!cancelled) setFetchLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [authLoading, isAuthenticated, user, getValidToken]);

  const handleCopy = useCallback(() => {
    if (!dashboard?.link) return;
    navigator.clipboard.writeText(dashboard.link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }, [dashboard]);

  const handleShare = useCallback(async () => {
    if (!dashboard?.link) return;
    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await navigator.share({ url: dashboard.link });
        return;
      } catch {
        // user cancelled or not supported — fall through to copy
      }
    }
    handleCopy();
  }, [dashboard, handleCopy]);

  // Auth not yet resolved — nothing to render (page-level spinner handles this)
  if (authLoading) return null;

  // Not authenticated — nothing to show (page-level redirect handles this)
  if (!isAuthenticated) return null;

  return (
    <section className="bg-surface-light px-4 py-8 dark:bg-surface-dark">
      <div className="mx-auto max-w-3xl space-y-6">

        {/* ── Link section ── */}
        <h2 className="text-brand-navy dark:text-white type-h2">
          {t("dashboard.myCode")}
        </h2>

        {fetchLoading ? (
          <div className="flex items-center gap-3 rounded-xl border border-gray-100 dark:border-white/10 bg-white dark:bg-surface-dark-raised px-4 py-3 shadow-sm">
            <Loader2 className="h-4 w-4 animate-spin text-brand-red" />
            <span className="text-sm text-gray-500 dark:text-gray-400">{t("dashboard.loading")}</span>
          </div>
        ) : fetchError ? (
          <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
            {t(`dashboard.${fetchError}`)}
          </div>
        ) : dashboard ? (
          <>
            {/* Link input + copy */}
            <div className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-surface-dark-raised px-4 py-3 shadow-sm">
              <span className="min-w-0 flex-1 truncate text-sm text-gray-600 dark:text-gray-300">
                {dashboard.link}
              </span>
              <button
                onClick={handleCopy}
                aria-label={copied ? t("dashboard.copied") : t("dashboard.copyLink")}
                title={copied ? t("dashboard.copied") : t("dashboard.copyLink")}
                className="shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 dark:hover:bg-white/10 hover:text-brand-navy dark:hover:text-white"
              >
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleShare}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-brand-navy dark:border-white/30 bg-transparent px-4 py-3 text-sm font-bold text-brand-navy dark:text-white transition-colors hover:bg-brand-navy/5 dark:hover:bg-white/5"
              >
                <Share2 className="h-4 w-4" />
                {t("dashboard.share")}
              </button>
              <button
                onClick={() => setShowQR(true)}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand-red px-4 py-3 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg hover:bg-brand-red-dark"
              >
                <QrCode className="h-4 w-4" />
                {t("dashboard.generateQR")}
              </button>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4 rounded-2xl border border-gray-100 dark:border-white/10 bg-white dark:bg-surface-dark-raised p-5 shadow-sm">
              <StatCard label={t("dashboard.stats.recommended")} value={dashboard.recommended_count} />
              <StatCard label={t("dashboard.stats.accepted")} value={dashboard.accepted_count} />
              <StatCard label={t("dashboard.stats.completed")} value={dashboard.completed_count} />
            </div>

            {/* History */}
            <h3 className="text-xl font-black text-brand-navy dark:text-white">
              {t("dashboard.history.title")}
            </h3>
            {dashboard.referrals.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">{t("dashboard.history.empty")}</p>
            ) : (
              <div className="space-y-2">
                {dashboard.referrals.map((ref, idx) => (
                  <ReferralRow key={`${ref.email}-${idx}`} referral={ref} t={t} />
                ))}
              </div>
            )}
          </>
        ) : null}

        {showQR && dashboard?.link && (
          <QRModal link={dashboard.link} onClose={() => setShowQR(false)} t={t} />
        )}
      </div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function RecomiendaYGanaPage({ locale }: { locale: string }) {
  const basePath = locale === routing.defaultLocale ? "" : `/${locale}`;
  const t = useTranslations("referralPage");
  const steps = t.raw("steps") as Array<{ title: string; description: string }>;
  const quickSteps = t.raw("quickSteps") as string[];
  const qrCards = t.raw("qrCards") as Array<{ label: string; description: string }>;
  const qrItems = [
    {
      href: IOS_APP_URL,
      qrSrc: "/static/referrals/qr-ios-geesol.png",
      store: t("app.store.apple"),
    },
    {
      href: ANDROID_APP_URL,
      qrSrc: "/static/referrals/qr-android-geesol.png",
      store: t("app.store.google"),
    },
  ];
  const platform = usePlatform();
  const heroImages = [
    "/static/home/hero-illustration.webp",
    "/static/home/hero-illustration-carousel-01.webp",
    "/static/home/hero-illustration-carousel-02.webp",
  ];

  return (
    <div className="min-h-screen bg-surface-light text-ink dark:bg-surface-dark dark:text-gray-100">
      {/* <HeroHighlight containerClassName="min-h-[78vh] lg:min-h-0 bg-surface-light dark:bg-surface-dark"> */}
        <div className="relative mx-auto flex w-full max-w-7xl flex-col gap-6 sm:gap-12 lg:gap-8 px-3 sm:px-4 pt-2 sm:pt-8 lg:pt-10 pb-12 sm:pb-16 lg:pb-10 lg:flex-row lg:items-stretch">
          <div className="absolute -left-40 -top-20 hidden h-56 w-56 rounded-full bg-gold blur-xl lg:block" />
          <div className="absolute -bottom-20 -right-20 h-40 w-40 rounded-full bg-blue/10 blur-xl sm:h-56 sm:w-56 lg:-bottom-24 lg:right-0" />

          {/* Texto primero para que el contenido principal quede visible en el HTML inicial */}
          <div className="relative z-10 order-1 flex-1 space-y-4 text-left sm:space-y-6 lg:order-1">
            <h1 className="break-words text-brand-navy dark:text-white type-h1">
              <Highlight className="from-gold to-gold-deep text-brand-navy">
                {t("hero.title")}
              </Highlight>
            </h1>
            <p className="max-w-2xl text-base leading-relaxed text-gray-700 dark:text-gray-300 md:text-xl sm:text-lg">
              {t("hero.subtitle")}
            </p>
            <div className="flex flex-col gap-6 pt-4 sm:gap-10 sm:pt-6 lg:flex-row lg:items-center">
              {platform === "ios" ? (
                <Button asChild className="w-full px-6 py-2.5 text-base uppercase sm:w-auto sm:px-8 sm:py-3 sm:text-lg">
                  <Link href={IOS_APP_URL} target="_blank" className="flex items-center justify-center gap-2">
                    <IconBrandAppstore className="h-5 w-5" />
                    {t("app.store.apple")}
                  </Link>
                </Button>
              ) : platform === "android" ? (
                <Button asChild className="w-full px-6 py-2.5 text-base uppercase sm:w-auto sm:px-8 sm:py-3 sm:text-lg">
                  <Link href={ANDROID_APP_URL} target="_blank" className="flex items-center justify-center gap-2">
                    <IconBrandGooglePlay className="h-5 w-5" />
                    {t("app.store.google")}
                  </Link>
                </Button>
              ) : (
                <Button asChild className="w-full px-6 py-2.5 text-base uppercase sm:w-auto sm:px-8 sm:py-3 sm:text-lg">
                  <Link href="#app-movil" className="flex items-center justify-center gap-2">
                    <IconBrandAppstore className="h-5 w-5" />
                    {t("hero.downloadCta")}
                  </Link>
                </Button>
              )}
            </div>
          </div>

          {/* Image / Carousel */}
          <div className="relative z-10 order-2 flex-1 lg:order-2">
            <HeroCarousel alt={t("hero.title")} initialImages={heroImages} />
          </div>
        </div>
      {/* </HeroHighlight> */}

      {/* ── Referral Dashboard (authenticated users only) ── */}
      <ReferralDashboard />

      <section className="px-4 py-8 sm:section-y">
        <div className="mx-auto max-w-6xl">
          <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 md:grid md:grid-cols-3 md:overflow-visible md:pb-0">
            {quickSteps.map((title, idx) => (
              <div
                key={title}
                className="min-w-[250px] snap-start rounded-card border border-brand-navy/10 bg-white/95 p-5 text-left shadow-card backdrop-blur sm:min-w-[280px] dark:border-white/10 dark:bg-surface-dark-raised/90 dark:shadow-card-dark md:min-w-0"
              >
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-brand-navy text-white dark:bg-brand-red">
                  {idx + 1}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-brand-navy dark:text-white">{title}</h3>
                <p className="mt-2 text-sm sm:text-base text-gray-700 dark:text-gray-300">
                  {t(`quickStepsDescriptions.${idx}`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="app-movil" className="scroll-mt-16 sm:scroll-mt-20 bg-white px-4 section-y dark:bg-surface-dark">
        <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[1.02fr_0.98fr]">
          <div className="surface-card-soft border border-brand-navy/10 p-6 dark:border-white/10 sm:p-7">
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-eyebrow text-brand-red">
              {t("app.eyebrow")}
            </p>
            <h2 className="mt-3 text-brand-navy dark:text-white type-h2">
              {t("app.title")}
            </h2>
            <p className="mt-3 text-sm sm:text-base text-gray-700 dark:text-gray-300">
              {t("app.description")}
            </p>
            <div className="mt-5 grid gap-3">
              <Button asChild className="rounded-full bg-brand-navy px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl dark:bg-brand-navy dark:hover:bg-brand-navy-dark">
                <Link href={IOS_APP_URL} target="_blank" className="flex w-full items-center justify-center gap-2">
                  <IconBrandAppstore />
                  {t("app.store.apple")}
                </Link>
              </Button>
              <Button asChild className="rounded-full bg-brand-red px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl dark:bg-brand-red dark:hover:bg-brand-red-dark">
                <Link href={ANDROID_APP_URL} target="_blank" className="flex w-full items-center justify-center gap-2">
                  <IconBrandGooglePlay />
                  {t("app.store.google")}
                </Link>
              </Button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            {qrCards.map((card, idx) => {
              const qrItem = qrItems[idx];
              if (!qrItem) return null;
              return (
                <Link
                  key={card.label}
                  href={qrItem.href}
                  target="_blank"
                  className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-brand-navy/10 bg-surface-light p-4 text-center shadow-card transition hover:shadow-card dark:border-white/10 dark:bg-surface-dark-raised dark:shadow-card-dark dark:hover:shadow-card-dark"
                >
                  <Image
                    src={qrItem.qrSrc}
                    alt={t("app.qrLabel", { store: qrItem.store })}
                    width={176}
                    height={176}
                    className="h-44 w-44 rounded-xl border border-black/5 bg-white object-contain p-2 dark:border-white/10"
                  />
                  <p className="text-sm font-semibold text-brand-navy dark:text-white">{card.label}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-surface-light px-4 section-y dark:bg-surface-dark-raised">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-3 md:grid-cols-[0.48fr_0.52fr] md:items-end">
            <h3 className="text-2xl sm:text-3xl font-bold text-brand-navy dark:text-white">
              {t("how.title")}
            </h3>
            <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 md:text-right">
              {t("how.subtitle")}
            </p>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {steps.map((step, idx) => (
              <div
                key={`${step.title}-${idx}`}
                className="rounded-2xl border border-brand-navy/10 bg-white p-5 text-left shadow-card dark:border-white/10 dark:bg-surface-dark dark:shadow-card-dark"
              >
                <p className="text-xs font-semibold uppercase tracking-eyebrow text-brand-red">
                  {idx + 1}
                </p>
                <h4 className="mt-2 text-lg font-semibold text-brand-navy dark:text-white">
                  {step.title}
                </h4>
                <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-navy px-4 section-y sm:section-y text-white dark:bg-surface-dark-deep">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center">
          <h3 className="text-2xl sm:text-3xl font-black">{t("cta.title")}</h3>
          <p className="text-base sm:text-lg text-mist">
            {t("cta.subtitle")}
          </p>
          <Button asChild className="w-full sm:w-auto rounded-lg border-2 border-white px-8 py-3 text-base sm:text-lg font-bold uppercase text-white bg-transparent hover:bg-white hover:text-brand-navy transition-colors">
            <Link href={`${basePath}/contacto`} className="flex items-center justify-center gap-3">
              <IconArrowRight className="h-5 w-5" />
              {t("cta.button")}
            </Link>
          </Button>
        </div>
      </section>

      <BackToTopButton />
    </div>
  );
}
